import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, doc, deleteDoc, getDocs, getDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ko, enUS } from 'date-fns/locale';

interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  price: number;
}

interface Invoice {
  id: string;
  cardId?: string; // 카드 ID 추가
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;
  customerName: string;
  customerCompany: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
  documentType: string;
  documentTitle: string;
  status?: string;
  language?: string;
  pdfUrl?: string;
  pdfPath?: string;
  pdfFileName?: string;
  createdAt?: Date;
  updatedAt?: Date;
  totalAmount?: number;
  subtotal?: number;
  taxAmount?: number;
}

const InvoiceListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cardId: initialCardId } = useParams();
  const userId = getAuth().currentUser?.uid;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cardIdFilter, setCardIdFilter] = useState(initialCardId || null);

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const result = t(key, options);
      return typeof result === 'string' ? result : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount: any) => {
    let numAmount = amount;
    
    if (typeof numAmount === 'string') {
      const cleanedString = numAmount.replace(/[^0-9.-]/g, '');
      numAmount = parseFloat(cleanedString);
    }
    
    if (numAmount === undefined || numAmount === null || numAmount === '') {
      numAmount = 0;
    }
    
    if (typeof numAmount !== 'number' || isNaN(numAmount)) {
      numAmount = 0;
    }
    
    const currencySymbol = safeT('common.currencySymbol') || '$';
    const currentLanguage = i18n.language;
    let formattedAmount;
    
    if (currentLanguage === 'ko') {
      formattedAmount = numAmount.toLocaleString('ko-KR');
    } else if (currentLanguage === 'en') {
      formattedAmount = numAmount.toLocaleString('en-US');
    } else {
      formattedAmount = numAmount.toLocaleString();
    }
    
    return `${currencySymbol}${formattedAmount}`;
  };

  // Helper function to format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) {
      return '날짜 없음';
    }
    let date;
    if (typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return '날짜 없음';
    }
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  // Filter invoices based on search term and status
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filterStatus);
    }

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => {
        const invoiceNumber = invoice.invoiceNumber || '';
        const customerName = invoice.customerName || '';
        const customerCompany = invoice.customerCompany || '';
        
        return invoiceNumber.toLowerCase().includes(lowerCaseSearchTerm) ||
               customerName.toLowerCase().includes(lowerCaseSearchTerm) ||
               customerCompany.toLowerCase().includes(lowerCaseSearchTerm);
      });
    }
    return filtered;
  }, [invoices, filterStatus, searchTerm]);

  const fetchInvoices = useCallback(async () => {
    if (!userId) {
      console.warn('사용자 ID가 없습니다.');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const db = getFirestore();
      
      if (cardIdFilter) {
        // 특정 카드의 인보이스 조회 (앱과 동일한 경로)
        const invoicesRef = collection(db, 'users', userId, 'cards', cardIdFilter, 'invoices');
        const snapshot = await getDocs(invoicesRef);
        
        const invoiceList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            cardId: cardIdFilter, // 카드 ID 추가
            ...data,
            totalAmount: calculateTotalAmount(data),
            issueDate: data.issueDate && typeof data.issueDate.toDate === 'function'
              ? data.issueDate.toDate()
              : data.issueDate,
            dueDate: data.dueDate && typeof data.dueDate.toDate === 'function'
              ? data.dueDate.toDate()
              : data.dueDate,
            status: data.status || 'pending'
          } as any;
        });

        setInvoices(invoiceList);
      } else {
        // 사용자의 모든 카드에서 인보이스 조회 (앱 기준)
        const cardsRef = collection(db, 'users', userId, 'cards');
        const cardsSnapshot = await getDocs(cardsRef);
        
        const allInvoices: any[] = [];
        
        // 각 카드에서 인보이스 가져오기
        for (const cardDoc of cardsSnapshot.docs) {
          const cardId = cardDoc.id;
          const cardInvoicesRef = collection(db, 'users', userId, 'cards', cardId, 'invoices');
          const cardInvoicesSnapshot = await getDocs(cardInvoicesRef);
          
          const cardInvoices = cardInvoicesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              cardId: cardId, // 카드 ID 추가
              ...data,
              totalAmount: calculateTotalAmount(data),
              issueDate: data.issueDate && typeof data.issueDate.toDate === 'function'
                ? data.issueDate.toDate()
                : data.issueDate,
              dueDate: data.dueDate && typeof data.dueDate.toDate === 'function'
                ? data.dueDate.toDate()
                : data.dueDate,
              status: data.status || 'pending'
            };
          });
          
          allInvoices.push(...cardInvoices);
        }
        
        // 날짜순으로 정렬
        allInvoices.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
          const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        
        setInvoices(allInvoices);
      }
    } catch (error) {
      console.error('청구서 목록을 가져오는 중 오류 발생:', error);
      alert(safeT('invoiceList.loadError'));
    } finally {
      setLoading(false);
    }
  }, [userId, cardIdFilter]);

  // totalAmount 계산 함수
  const calculateTotalAmount = (data: any) => {
    let totalAmount = data.totalAmount;
    
    if (totalAmount === undefined || totalAmount === null) {
      let subtotal = 0;
      if (data.items && Array.isArray(data.items)) {
        subtotal = data.items.reduce((sum: number, item: any) => {
          const price = parseFloat(item.price) || 0;
          const quantity = parseFloat(item.quantity) || 1;
          return sum + (price * quantity);
        }, 0);
      }
      
      const taxRate = parseFloat(data.taxRate) || 0;
      const taxAmount = subtotal * (taxRate / 100);
      totalAmount = subtotal + taxAmount;
    } else {
      if (typeof totalAmount === 'string') {
        const cleanedAmount = totalAmount.replace(/[^0-9.-]/g, '');
        totalAmount = parseFloat(cleanedAmount);
      } else if (typeof totalAmount !== 'number') {
        totalAmount = parseFloat(totalAmount);
      }
    }
    
    if (typeof totalAmount !== 'number' || isNaN(totalAmount)) {
      totalAmount = 0;
    }
    
    return totalAmount;
  };

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDelete = async (invoiceId: string) => {
    const invoiceToDelete = invoices.find(inv => inv.id === invoiceId);
    const invoiceNumber = invoiceToDelete?.invoiceNumber || '';
    
    const confirmDelete = window.confirm(
      `${safeT('invoiceList.deleteConfirm', { invoiceNumber })}\n${safeT('invoiceList.deleteConfirmMessage')}`
    );
    
    if (confirmDelete) {
      try {
        const db = getFirestore();
        let docRef;

        if (cardIdFilter) {
          docRef = doc(db, 'users', userId!, 'cards', cardIdFilter, 'invoices', invoiceId);
        } else {
          // 카드 ID가 없는 경우 모든 카드에서 찾기
          const cardsRef = collection(db, 'users', userId!, 'cards');
          const cardsSnapshot = await getDocs(cardsRef);
          
          for (const cardDoc of cardsSnapshot.docs) {
            const cardId = cardDoc.id;
            const invoiceRef = doc(db, 'users', userId!, 'cards', cardId, 'invoices', invoiceId);
            const invoiceSnap = await getDoc(invoiceRef);
            
            if (invoiceSnap.exists) {
              docRef = invoiceRef;
              break;
            }
          }
        }

        if (docRef) {
          await deleteDoc(docRef);
          alert(safeT('invoiceList.deleteSuccess'));
          fetchInvoices();
        } else {
          alert('인보이스를 찾을 수 없습니다.');
        }
      } catch (error) {
        console.error('청구서 삭제 중 오류 발생:', error);
        alert(safeT('invoiceList.deleteError'));
      }
    }
  };

  const handleEditInvoice = (invoiceId: string) => {
    const invoiceToEdit = invoices.find(inv => inv.id === invoiceId);
    console.log('handleEditInvoice - invoiceId:', invoiceId, 'invoiceToEdit:', invoiceToEdit);
    
    if (invoiceToEdit) {
      // 새로운 라우팅 경로로 수정: /invoices/:invoiceId/edit
      navigate(`/invoices/${invoiceId}/edit`);
    } else {
      console.error('handleEditInvoice - invoice not found for id:', invoiceId);
      alert(safeT('invoiceList.invoiceNotFound'));
    }
  };

  const handleOpenPdf = async (invoice: Invoice) => {
    console.log('[InvoiceListPage] handleOpenPdf called. Invoice data:', { 
      pdfUrl: invoice.pdfUrl, 
      pdfPath: invoice.pdfPath, 
      pdfFileName: invoice.pdfFileName, 
      invoiceId: invoice.id 
    });
    
    if (!invoice.pdfUrl && !invoice.pdfPath) {
      alert(safeT('invoiceListScreen.pdfError'));
      return;
    }
    
    try {
      if (invoice.pdfUrl) {
        console.log('[InvoiceListPage] handleOpenPdf: PDF URL detected.', invoice.pdfUrl);
        
        // 웹에서는 새 탭에서 PDF 열기
        const newWindow = window.open(invoice.pdfUrl, '_blank');
        
        if (!newWindow) {
          // 팝업이 차단된 경우 다운로드 링크 생성
          const link = document.createElement('a');
          link.href = invoice.pdfUrl;
          link.download = invoice.pdfFileName || `invoice_${invoice.id}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
      } else if (invoice.pdfPath) {
        console.log('[InvoiceListPage] handleOpenPdf: PDF Path detected.', invoice.pdfPath);
        
        // 웹에서는 로컬 파일 경로를 직접 열 수 없으므로 경고
        alert(safeT('invoiceListScreen.localPdfNotSupported'));
        console.warn('[InvoiceListPage] handleOpenPdf: Local PDF path not supported in web version:', invoice.pdfPath);
      }
    } catch (error) {
      console.error('[InvoiceListPage] handleOpenPdf: Critical error during PDF open process:', error);
      alert(`${safeT('invoiceListScreen.openError')}\n\n오류: ${(error as Error)?.message || error?.toString() || String(error)}`);
    }
  };

  const handleShare = async (invoice: Invoice) => {
    console.log('[InvoiceListPage] handleShare called. Invoice data:', { 
      pdfUrl: invoice.pdfUrl, 
      pdfPath: invoice.pdfPath, 
      pdfFileName: invoice.pdfFileName, 
      invoiceId: invoice.id 
    });
    
    if (!invoice || (!invoice.pdfUrl && !invoice.pdfPath)) {
      alert(safeT('invoiceListScreen.shareError'));
      return;
    }
    
    try {
      const shareFileName = invoice.pdfFileName || invoice.invoiceNumber || `invoice_${invoice.id}.pdf`;
      const shareTitle = safeT('invoiceListScreen.shareTitle', { number: invoice.invoiceNumber || invoice.id });
      const shareMessage = safeT('invoiceListScreen.shareMessage', { 
        number: invoice.invoiceNumber || invoice.id, 
        customer: invoice.customerName || safeT('invoiceListScreen.customerDefault') 
      });
      
      if (navigator.share && invoice.pdfUrl) {
        // Web Share API 사용 (모바일 브라우저에서 지원)
        await navigator.share({
          title: shareTitle,
          text: shareMessage,
          url: invoice.pdfUrl
        });
      } else if (invoice.pdfUrl) {
        // 데스크톱 브라우저에서는 URL 복사
        await navigator.clipboard.writeText(invoice.pdfUrl);
        alert(safeT('invoiceListScreen.urlCopied'));
      } else {
        // PDF 경로만 있는 경우 (웹에서는 지원하지 않음)
        alert(safeT('invoiceListScreen.shareNotSupported'));
      }
    } catch (error) {
      console.error('[InvoiceListPage] handleShare: Error during share process:', error);
      
      // Web Share API 실패 시 URL 복사로 폴백
      if (invoice.pdfUrl) {
        try {
          await navigator.clipboard.writeText(invoice.pdfUrl);
          alert(safeT('invoiceListScreen.urlCopied'));
        } catch (clipboardError) {
          console.error('[InvoiceListPage] handleShare: Clipboard write failed:', clipboardError);
          alert(safeT('invoiceListScreen.shareError'));
        }
      } else {
        alert(safeT('invoiceListScreen.shareError'));
      }
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'paid':
        return { 
          backgroundColor: '#34D399', 
          textColor: '#FFFFFF', 
          text: safeT('invoiceListScreen.completed') 
        };
      case 'pending':
        return { 
          backgroundColor: '#FBBF24', 
          textColor: '#FFFFFF', 
          text: safeT('invoiceListScreen.pending') 
        };
      case 'overdue':
        return { 
          backgroundColor: '#F87171', 
          textColor: '#FFFFFF', 
          text: safeT('invoiceListScreen.overdue') 
        };
      case 'cancelled':
        return { 
          backgroundColor: '#D1D5DB', 
          textColor: '#374151', 
          text: safeT('invoiceListScreen.cancelled') 
        };
      default:
        return { 
          backgroundColor: '#D1D5DB', 
          textColor: '#374151', 
          text: status || safeT('invoiceListScreen.unknownStatus') 
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('invoiceListScreen.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="ml-4 text-lg font-medium text-gray-900">
                {cardIdFilter ? safeT('invoiceListScreen.cardInvoices') : safeT('invoiceListScreen.allInvoices')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={safeT('invoiceListScreen.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">{safeT('invoiceListScreen.allStatus')}</option>
                <option value="paid">{safeT('invoiceListScreen.completed')}</option>
                <option value="pending">{safeT('invoiceListScreen.pending')}</option>
                <option value="overdue">{safeT('invoiceListScreen.overdue')}</option>
                <option value="cancelled">{safeT('invoiceListScreen.cancelled')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!loading && (!Array.isArray(filteredInvoices) || filteredInvoices.length === 0) ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm || filterStatus !== 'all' ? safeT('invoiceListScreen.noSearchResults') : safeT('invoiceListScreen.noInvoices')}
            </h3>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredInvoices.map((invoice) => {
              const statusStyle = getStatusStyle(invoice.status || 'pending');
              const isOverdue = invoice.status === 'pending' && invoice.dueDate && new Date(invoice.dueDate) < new Date();

              return (
                <div key={invoice.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber || safeT('invoiceListScreen.invoiceNumberDefault')}
                      </h3>
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: statusStyle.backgroundColor,
                          color: statusStyle.textColor
                        }}
                      >
                        {statusStyle.text}
                      </span>
                    </div>

                    {/* Customer Info */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-900">
                        {invoice.customerName && invoice.customerName.trim() !== '' 
                          ? invoice.customerName 
                          : (invoice.customerEmail ? invoice.customerEmail.split('@')[0] : safeT('invoiceListScreen.customerNameDefault'))}
                      </p>
                      {invoice.customerCompany && (
                        <p className="text-sm text-gray-500">{invoice.customerCompany}</p>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="mb-4 space-y-1">
                      <p className="text-sm text-gray-500">
                        {safeT('invoiceListScreen.issueDate')}: {formatDate(invoice.issueDate)}
                      </p>
                      {invoice.dueDate && (
                        <p className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {safeT('invoiceListScreen.dueDate')}: {formatDate(invoice.dueDate)}
                        </p>
                      )}
                    </div>

                    {/* Total Amount */}
                    <div className="mb-4">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatCurrency(invoice.totalAmount || 0)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenPdf(invoice)}
                        className="flex-1 flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {safeT('invoiceListScreen.open')}
                      </button>
                      
                      <button
                        onClick={() => handleShare(invoice)}
                        className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                        {safeT('invoiceListScreen.share')}
                      </button>
                    </div>

                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => handleEditInvoice(invoice.id)}
                        className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {safeT('invoiceListScreen.edit')}
                      </button>
                      
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="flex-1 flex items-center justify-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {safeT('invoiceListScreen.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceListPage;